import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { PrimaryButton } from "@components/Buttons";
import PermissionMatrixSection from "./PermissionMatrixSection";
import {
  getMatrixGroupsForPermission,
  hasPermissionMatrix,
} from "@constants/permissionAccessMatrix";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  'input:not([disabled])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[role="button"]:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Click info icon / badge to open permission details (summary + access matrix).
 */
const PermissionInfoPopup = ({
  permissionCode,
  permissionLabel,
  children,
  "data-testid": dataTestIdProp,
}) => {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const titleId = useId();

  const dataTestId = dataTestIdProp || `permission-info-popup-${permissionCode || "unknown"}`;

  const summaryKey = `role.permission-descriptions.${permissionCode}.summary`;
  const hasDescription = Boolean(permissionCode && i18n.exists(summaryKey));
  const showMatrix = hasPermissionMatrix(permissionCode);

  const matrixGroups = useMemo(
    () =>
      showMatrix
        ? getMatrixGroupsForPermission(permissionCode, { hideDenied: true })
        : [],
    [permissionCode, showMatrix],
  );

  const closePopup = useCallback(() => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !hasDescription) return undefined;

    const focusFrame = requestAnimationFrame(() => {
      dialogRef.current
        ?.querySelector(`[data-testid="${dataTestId}-close-button"]`)
        ?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        closePopup();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen, hasDescription, closePopup, dataTestId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!hasDescription) {
    return children ? <>{children}</> : null;
  }

  const summary = t(summaryKey);
  const displayName = permissionLabel || permissionCode;
  const ariaLabel = t("role.permission-info-aria", { name: displayName });

  const openPopup = (e) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const popup = isOpen
    ? createPortal(
        <div
          role="presentation"
          className={`fixed inset-0 ${colors.bgBlurModal} z-[60] flex items-center justify-center p-4 backdrop-blur-[2px]`}
          data-testid={`${dataTestId}-overlay`}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col ${colors.bgModal} border ${colors.borderPrimary} rounded-xl shadow-2xl overflow-hidden`}
            data-testid={dataTestId}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`h-1 w-full shrink-0 ${colors.buttonBackgroundGradient}`} />

            <div
              className={`flex items-start gap-3 px-6 pt-5 pb-4 border-b ${colors.borderPrimary} shrink-0`}
              data-testid={`${dataTestId}-header`}
            >
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${colors.bgIconAccent} ${colors.textActive}`}
                aria-hidden="true"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className={`mb-0.5 text-xs font-medium uppercase tracking-wide ${colors.textSubTitle}`}>
                  {t("role.permission-details", "Permission details")}
                </p>
                <h3
                  id={titleId}
                  className={`text-lg font-semibold leading-snug ${colors.textPrimary} break-words`}
                  data-testid={`${dataTestId}-title`}
                >
                  {displayName}
                </h3>
              </div>
            </div>

            <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0" data-testid={`${dataTestId}-content`}>
              <div
                className={`${colors.bgInput} border ${colors.borderPrimary} rounded-lg px-4 py-3`}
                data-testid={`${dataTestId}-summary-box`}
              >
                <p className={`mb-0 text-sm leading-relaxed ${colors.textPrimary} break-words`} data-testid={`${dataTestId}-summary`}>
                  {summary}
                </p>
              </div>

              {showMatrix && matrixGroups.length > 0 ? (
                <PermissionMatrixSection
                  groups={matrixGroups}
                  colors={colors}
                  t={t}
                  dataTestId={dataTestId}
                />
              ) : null}
            </div>

            <div
              className={`flex justify-end px-6 py-4 border-t ${colors.borderPrimary} shrink-0`}
              data-testid={`${dataTestId}-footer`}
            >
              <PrimaryButton
                type="button"
                size="md"
                onClick={closePopup}
                data-testid={`${dataTestId}-close-button`}
              >
                {t("common.close-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`${dataTestId}-trigger`}
        className={
          children
            ? "inline-flex max-w-full cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-400"
            : `ml-1.5 h-4 w-4 shrink-0 ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-400`
        }
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={openPopup}
      >
        {children || (
          <svg
            data-testid={`${dataTestId}-icon`}
            className="h-4 w-4"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
      {popup}
    </>
  );
};

export default PermissionInfoPopup;
